import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  BookOpen,
  Headphones,
  FileText,
  Video,
  Clock,
  Sparkles,
  AlertCircle,
  Pencil,
  Search,
  Wand2,
  Loader2,
  RefreshCw,
  Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Reuses the same lightweight markdown renderer style as CourseDetail
function renderLessonBody(md = "") {
  const blocks = md.split(/\n{2,}/);
  return blocks.map((b, i) => {
    if (b.startsWith("### "))
      return (
        <h3 key={i} className="font-display text-lg mt-5 mb-2 text-white">
          {b.slice(4)}
        </h3>
      );
    if (b.startsWith("## "))
      return (
        <h2 key={i} className="font-display text-xl mt-6 mb-2 text-white">
          {b.slice(3)}
        </h2>
      );
    if (b.startsWith("> "))
      return (
        <blockquote
          key={i}
          className="border-l-2 border-brand-pink pl-4 italic text-white/70 my-3"
        >
          {b.slice(2)}
        </blockquote>
      );
    if (/^\d+\.\s/.test(b) || b.startsWith("- ")) {
      const items = b.split("\n").map((li) => li.replace(/^(\d+\.\s|-\s)/, ""));
      const Tag = /^\d+\./.test(b) ? "ol" : "ul";
      return (
        <Tag
          key={i}
          className={`${Tag === "ol" ? "list-decimal" : "list-disc"} pl-5 space-y-1 my-3 text-white/85`}
        >
          {items.map((li, j) => (
            <li key={j}>{li}</li>
          ))}
        </Tag>
      );
    }
    const parts = b
      .split(/(\*\*[^*]+\*\*)/g)
      .map((p, j) =>
        p.startsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : p
      );
    return (
      <p key={i} className="text-white/85 leading-relaxed my-3">
        {parts}
      </p>
    );
  });
}

function ContentBadges({ lesson }) {
  const hasWritten = !!(lesson.body_md && lesson.body_md.trim());
  const hasAudio = !!lesson.audio_url;
  const items = [
    { on: hasWritten, label: "Written", Icon: FileText },
    { on: hasAudio, label: "Audio", Icon: Headphones },
  ];
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map(({ on, label, Icon }) => (
        <span
          key={label}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tracking-widest uppercase ${
            on
              ? "bg-brand-pink/20 text-brand-pink border border-brand-pink/40"
              : "bg-white/5 text-white/40 border border-white/10"
          }`}
        >
          <Icon className="w-2.5 h-2.5" /> {label}
        </span>
      ))}
    </div>
  );
}

function LessonCard({ courseId, moduleId, lesson, onLessonChanged }) {
  const hasWritten = !!(lesson.body_md && lesson.body_md.trim());
  const hasAudio = !!lesson.audio_url;
  const isEmpty = !hasWritten && !hasAudio;
  const [busy, setBusy] = useState(false);
  const [editingAudio, setEditingAudio] = useState(false);
  const [audioDraft, setAudioDraft] = useState(lesson.audio_url || "");

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/courses/${courseId}/lessons/generate`,
        { module_id: moduleId, lesson_id: lesson.id, overwrite: hasWritten }
      );
      if (data.skipped) {
        toast.info("Skipped — pass Regenerate to overwrite.");
      } else {
        toast.success(`Written by Kristy AI · ${data.body_md.split(" ").length} words`);
        onLessonChanged?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed.");
    } finally {
      setBusy(false);
    }
  };

  const saveAudio = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/courses/${courseId}/lessons/audio`, {
        module_id: moduleId,
        lesson_id: lesson.id,
        audio_url: audioDraft.trim(),
      });
      toast.success(audioDraft.trim() ? "HeyGen narration attached." : "Narration removed.");
      setEditingAudio(false);
      onLessonChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save audio URL.");
    } finally {
      setBusy(false);
    }
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewScript, setPreviewScript] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);

  const narrate = async () => {
    if (!hasWritten) {
      toast.error("Write the lesson body first — narration reads the body aloud.");
      return;
    }
    setBusy(true);
    toast.info("Sending to HeyGen — usually takes 15-30 seconds.");
    try {
      const { data } = await api.post(
        `/admin/courses/${courseId}/lessons/narrate`,
        { module_id: moduleId, lesson_id: lesson.id, overwrite: hasAudio }
      );
      if (data.skipped) {
        toast.info("Skipped — audio already exists.");
      } else {
        toast.success(`Narration ready · ${Math.round(data.duration || 0)}s`);
        onLessonChanged?.();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "HeyGen narration failed.");
    } finally {
      setBusy(false);
    }
  };

  const previewScriptFn = async () => {
    if (!hasWritten) {
      toast.error("Write the lesson body first.");
      return;
    }
    setPreviewBusy(true);
    try {
      const { data } = await api.post(
        `/admin/courses/${courseId}/lessons/narrate-preview`,
        { module_id: moduleId, lesson_id: lesson.id }
      );
      setPreviewScript(data.script || "");
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setPreviewBusy(false);
    }
  };

  return (
    <div
      data-testid={`preview-lesson-${lesson.id}`}
      className="rounded-2xl border border-white/10 bg-black/40 p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-white">{lesson.title}</div>
          <div className="text-xs text-white/50 mt-0.5 inline-flex items-center gap-2">
            <Clock className="w-3 h-3" /> {lesson.duration}
            {lesson.read_minutes ? ` · ${lesson.read_minutes} min read` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ContentBadges lesson={lesson} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Button
          size="sm"
          onClick={generate}
          disabled={busy}
          data-testid={`lesson-generate-${lesson.id}`}
          className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white h-8 px-3 text-xs"
        >
          {busy ? (
            <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Writing…</>
          ) : hasWritten ? (
            <><RefreshCw className="w-3 h-3 mr-1.5" /> Regenerate with AI</>
          ) : (
            <><Wand2 className="w-3 h-3 mr-1.5" /> Write with Kristy AI</>
          )}
        </Button>
        <Button
          size="sm"
          onClick={() => { setEditingAudio(!editingAudio); setAudioDraft(lesson.audio_url || ""); }}
          disabled={busy}
          data-testid={`lesson-audio-toggle-${lesson.id}`}
          variant="ghost"
          className="rounded-full h-8 px-3 text-xs text-white/70 hover:text-white hover:bg-white/5"
        >
          <Headphones className="w-3 h-3 mr-1.5" /> {hasAudio ? "Change audio URL" : "Paste MP3 URL"}
        </Button>
        <Button
          size="sm"
          onClick={previewScriptFn}
          disabled={previewBusy || !hasWritten}
          data-testid={`lesson-preview-script-${lesson.id}`}
          variant="ghost"
          className="rounded-full h-8 px-3 text-xs text-white/70 hover:text-white hover:bg-white/5"
        >
          {previewBusy ? (
            <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Loading…</>
          ) : (
            <><Wand2 className="w-3 h-3 mr-1.5" /> Preview script</>
          )}
        </Button>
        <Button
          size="sm"
          onClick={narrate}
          disabled={busy || !hasWritten}
          data-testid={`lesson-narrate-${lesson.id}`}
          variant="outline"
          className="rounded-full h-8 px-3 text-xs bg-black/40 border-brand-pink/40 text-brand-pink hover:bg-brand-pink/10"
        >
          {busy ? (
            <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Narrating…</>
          ) : (
            <><Headphones className="w-3 h-3 mr-1.5" /> {hasAudio ? "Regenerate narration" : "Narrate with HeyGen"}</>
          )}
        </Button>
      </div>

      {previewOpen && (
        <div
          data-testid={`lesson-script-preview-${lesson.id}`}
          className="mb-3 rounded-xl bg-white/[0.03] border border-brand-pink/30 p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-brand-pink/80">
              HeyGen will read this ({previewScript.length} chars)
            </div>
            <button
              type="button"
              onClick={() => setPreviewOpen(false)}
              className="text-xs text-white/60 hover:text-white"
            >
              Close
            </button>
          </div>
          <div className="max-h-56 overflow-auto text-sm text-white/80 whitespace-pre-wrap leading-relaxed">
            {previewScript}
          </div>
        </div>
      )}

      {editingAudio && (
        <div className="mb-3 rounded-xl bg-white/[0.03] border border-white/10 p-3 flex flex-col md:flex-row gap-2 items-stretch md:items-center">
          <input
            type="url"
            value={audioDraft}
            onChange={(e) => setAudioDraft(e.target.value)}
            placeholder="https://... your HeyGen MP3 URL"
            data-testid={`lesson-audio-input-${lesson.id}`}
            className="flex-1 rounded-full bg-black/40 border border-white/10 text-white placeholder-white/40 px-4 py-2 text-sm outline-none focus:border-brand-pink"
          />
          <Button
            size="sm"
            onClick={saveAudio}
            disabled={busy}
            data-testid={`lesson-audio-save-${lesson.id}`}
            className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white h-9 px-4 text-xs"
          >
            <Save className="w-3 h-3 mr-1.5" /> Save
          </Button>
        </div>
      )}

      {isEmpty && !editingAudio && (
        <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-4 text-sm text-white/60 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-brand-pink mt-0.5 flex-shrink-0" />
          <div>Empty lesson. Click <b>Write with Kristy AI</b> above to draft this in seconds.</div>
        </div>
      )}

      {hasAudio && (
        <div className="rounded-xl bg-neutral-950/70 border border-white/10 p-4 mb-3 flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-brand-pink/20 flex items-center justify-center flex-shrink-0">
            <Headphones className="w-4 h-4 text-brand-pink" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.22em] uppercase text-brand-pink mb-1">
              Narrated by Kristy
            </div>
            <audio
              data-testid={`preview-audio-${lesson.id}`}
              controls
              src={lesson.audio_url}
              className="w-full"
            />
          </div>
        </div>
      )}

      {hasWritten && (
        <details className="group rounded-xl bg-white/[0.03] border border-white/10 p-4">
          <summary className="cursor-pointer text-sm text-white/80 inline-flex items-center gap-2 select-none">
            <FileText className="w-3.5 h-3.5 text-brand-pink" />
            Read the written lesson
            <span className="text-white/40 text-xs group-open:hidden">
              · click to expand
            </span>
          </summary>
          <article className="mt-3 max-w-none">
            {renderLessonBody(lesson.body_md)}
          </article>
        </details>
      )}

      {(lesson.summary || lesson.action_step) && (
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          {lesson.summary && (
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">
                Summary
              </div>
              <p className="text-sm text-white/80 leading-relaxed">
                {lesson.summary}
              </p>
            </div>
          )}
          {lesson.action_step && (
            <div className="rounded-xl bg-brand-pink/10 border border-brand-pink/30 p-3">
              <div className="text-[10px] uppercase tracking-widest text-brand-pink mb-1">
                Action Step
              </div>
              <p className="text-sm text-white leading-relaxed font-medium">
                {lesson.action_step}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CourseSection({ course, defaultOpen, onCourseChanged }) {
  const [bulkBusy, setBulkBusy] = useState(false);
  const [narrateBusy, setNarrateBusy] = useState(false);
  const totals = useMemo(() => {
    let lessons = 0,
      written = 0,
      audio = 0;
    course.modules.forEach((m) =>
      m.lessons.forEach((l) => {
        lessons += 1;
        if (l.body_md && l.body_md.trim()) written += 1;
        if (l.audio_url) audio += 1;
      })
    );
    return { lessons, written, audio };
  }, [course]);

  const completeness = totals.lessons
    ? Math.round((totals.written * 100) / totals.lessons)
    : 0;

  const missing = totals.lessons - totals.written;
  const missingAudio = totals.written - totals.audio;

  const fillAll = async () => {
    if (!missing) {
      if (!window.confirm(`Every lesson already has content. Regenerate all ${totals.lessons} lessons? This will overwrite existing bodies.`)) return;
    }
    setBulkBusy(true);
    toast.info(`Generating ${missing || totals.lessons} lessons — this can take 1-3 min.`);
    try {
      const { data } = await api.post(
        `/admin/courses/${course.id}/generate-all`,
        { overwrite: !missing }
      );
      toast.success(`${data.generated} written · ${data.skipped} skipped · ${data.errors} errors`);
      onCourseChanged?.();
    } catch (e) {
      toast.error("Bulk AI write failed. Try one lesson at a time.");
    } finally {
      setBulkBusy(false);
    }
  };

  const narrateAll = async () => {
    if (totals.written === 0) {
      toast.error("Write the lessons first — narration reads the body aloud.");
      return;
    }
    if (!missingAudio) {
      if (!window.confirm(`All ${totals.audio} lessons are already narrated. Regenerate every narration?`)) return;
    }
    setNarrateBusy(true);
    toast.info(`Narrating ${missingAudio || totals.written} lessons with your HeyGen voice — 1-3 min.`);
    try {
      const { data } = await api.post(
        `/admin/courses/${course.id}/narrate-all`,
        { overwrite: !missingAudio }
      );
      toast.success(`${data.generated} narrated · ${data.skipped} skipped · ${data.errors} errors`);
      onCourseChanged?.();
    } catch (e) {
      toast.error("Bulk narration failed. Try one lesson at a time.");
    } finally {
      setNarrateBusy(false);
    }
  };

  const renarrateAllClean = async () => {
    if (totals.written === 0) {
      toast.error("Write the lessons first.");
      return;
    }
    if (!window.confirm(`Re-narrate ALL ${totals.lessons} lessons with the new cleaned script? This will overwrite the existing ${totals.audio} narrations and use HeyGen credits for every lesson.`)) return;
    setNarrateBusy(true);
    toast.info(`Re-narrating ${totals.lessons} lessons with the fixed script — this can take 3-5 min.`);
    try {
      const { data } = await api.post(
        `/admin/courses/${course.id}/narrate-all`,
        { overwrite: true }
      );
      toast.success(`${data.generated} narrated · ${data.skipped} skipped · ${data.errors} errors`);
      onCourseChanged?.();
    } catch (e) {
      toast.error("Bulk re-narration failed. Try one lesson at a time.");
    } finally {
      setNarrateBusy(false);
    }
  };

  return (
    <div
      data-testid={`preview-course-${course.id}`}
      className="rounded-3xl border border-white/10 bg-black/30 overflow-hidden"
    >
      <div className="p-6 lg:p-8 flex flex-wrap items-start justify-between gap-4 border-b border-white/10 bg-gradient-to-br from-black/60 to-transparent">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.28em] uppercase text-brand-pink mb-2">
            {course.category} · {course.price === 0 ? "Free" : `$${course.price}`}
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-white">
            {course.title}
          </h2>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">{course.tagline}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              size="sm"
              onClick={fillAll}
              disabled={bulkBusy}
              data-testid={`course-fill-all-${course.id}`}
              className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white h-9 px-4 text-xs"
            >
              {bulkBusy ? (
                <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Writing all lessons…</>
              ) : missing ? (
                <><Wand2 className="w-3 h-3 mr-1.5" /> Fill {missing} empty lesson{missing === 1 ? "" : "s"} with Kristy AI</>
              ) : (
                <><RefreshCw className="w-3 h-3 mr-1.5" /> Regenerate every lesson</>
              )}
            </Button>
            <Button
              size="sm"
              onClick={narrateAll}
              disabled={narrateBusy || totals.written === 0}
              data-testid={`course-narrate-all-${course.id}`}
              variant="outline"
              className="rounded-full h-9 px-4 text-xs bg-black/40 border-brand-pink/40 text-brand-pink hover:bg-brand-pink/10"
            >
              {narrateBusy ? (
                <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Narrating…</>
              ) : missingAudio ? (
                <><Headphones className="w-3 h-3 mr-1.5" /> Narrate {missingAudio} lesson{missingAudio === 1 ? "" : "s"} with HeyGen</>
              ) : (
                <><RefreshCw className="w-3 h-3 mr-1.5" /> Re-narrate every lesson</>
              )}
            </Button>
            {totals.audio > 0 && (
              <Button
                size="sm"
                onClick={renarrateAllClean}
                disabled={narrateBusy || totals.written === 0}
                data-testid={`course-renarrate-clean-${course.id}`}
                variant="ghost"
                className="rounded-full h-9 px-4 text-xs text-white/70 hover:text-white hover:bg-white/5 border border-white/10"
                title="Overwrite ALL lessons with the new cleaned TTS script"
              >
                <RefreshCw className="w-3 h-3 mr-1.5" /> Re-narrate ALL (clean script)
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 md:gap-6 text-center">
          <div>
            <div className="font-display text-2xl text-white">
              {course.modules.length}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
              Modules
            </div>
          </div>
          <div>
            <div className="font-display text-2xl text-white">
              {totals.lessons}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
              Lessons
            </div>
          </div>
          <div>
            <div className="font-display text-2xl text-brand-pink">
              {totals.audio}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
              With Audio
            </div>
          </div>
          <div>
            <div className="font-display text-2xl text-brand-pink">
              {completeness}%
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
              Written
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-8">
        <Accordion
          type="multiple"
          defaultValue={defaultOpen ? course.modules.map((m) => m.id) : []}
          className="space-y-3"
        >
          {course.modules.map((m, mi) => (
            <AccordionItem
              key={m.id}
              value={m.id}
              className="rounded-2xl border border-white/10 bg-white/[0.02] px-5"
            >
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 text-left">
                  <span className="font-display text-xl text-brand-pink">
                    {String(mi + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <div className="font-display text-base text-white">
                      {m.title}
                    </div>
                    <div className="text-[11px] text-white/50">
                      {m.lessons.length} lessons
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <div className="space-y-3">
                  {m.lessons.map((l) => (
                    <LessonCard
                      key={l.id}
                      courseId={course.id}
                      moduleId={m.id}
                      lesson={l}
                      onLessonChanged={onCourseChanged}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}

export default function AdminCurriculum() {
  const [courses, setCourses] = useState(null);
  const [query, setQuery] = useState("");
  const [reloadTick, setReloadTick] = useState(0);

  const reload = () => setReloadTick((n) => n + 1);

  useEffect(() => {
    api
      .get("/courses")
      .then((r) => {
        const priority = (c) => {
          if (c.id === "bnb-cohost-academy") return 0;
          if (c.featured) return 1;
          return 2;
        };
        setCourses(
          [...r.data].sort((a, b) => {
            const p = priority(a) - priority(b);
            return p !== 0 ? p : (b.price || 0) - (a.price || 0);
          })
        );
      })
      .catch(() => setCourses([]));
  }, [reloadTick]);

  const filtered = useMemo(() => {
    if (!courses) return [];
    if (!query.trim()) return courses;
    const q = query.toLowerCase();
    return courses.filter((c) => {
      if (c.title.toLowerCase().includes(q) || c.category.toLowerCase().includes(q))
        return true;
      return c.modules.some(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.lessons.some((l) => l.title.toLowerCase().includes(q))
      );
    });
  }, [courses, query]);

  const rollup = useMemo(() => {
    if (!courses) return null;
    let lessons = 0,
      audio = 0,
      written = 0;
    courses.forEach((c) =>
      c.modules.forEach((m) =>
        m.lessons.forEach((l) => {
          lessons += 1;
          if (l.audio_url) audio += 1;
          if (l.body_md && l.body_md.trim()) written += 1;
        })
      )
    );
    return { courses: courses.length, lessons, audio, written };
  }, [courses]);

  if (courses === null)
    return <div className="text-white/50 p-10">Loading curriculum…</div>;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-2">
            Curriculum Preview
          </div>
          <h1 className="font-display text-4xl text-white">
            Everything you're selling
          </h1>
          <p className="text-white/60 text-sm mt-2 max-w-xl">
            Read the written body, hear any narration you've uploaded, and see
            exactly which lessons still need content — one place, no enrollment
            required.
          </p>
        </div>
        <div className="flex-1 min-w-[260px] max-w-md relative">
          <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a course, module, or lesson…"
            data-testid="curriculum-search"
            className="w-full rounded-full bg-white/5 border border-white/10 text-white placeholder-white/40 pl-11 pr-4 py-2.5 text-sm outline-none focus:border-brand-pink"
          />
        </div>
      </div>

      {rollup && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {[
            { k: rollup.courses, v: "Courses", Icon: BookOpen },
            { k: rollup.lessons, v: "Total lessons", Icon: Sparkles },
            { k: rollup.written, v: "Lessons written", Icon: FileText },
            { k: rollup.audio, v: "Lessons narrated", Icon: Headphones },
          ].map(({ k, v, Icon }) => (
            <div
              key={v}
              className="rounded-2xl border border-white/10 bg-black/40 p-5"
            >
              <Icon className="w-4 h-4 text-brand-pink mb-3" />
              <div className="font-display text-3xl text-white">{k}</div>
              <div className="text-[11px] uppercase tracking-widest text-white/50 mt-1">
                {v}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end mb-4">
        <Link
          to="/admin/courses"
          data-testid="curriculum-edit-link"
          className="text-brand-pink text-sm inline-flex items-center gap-2 hover:underline"
        >
          <Pencil className="w-3.5 h-3.5" /> Edit lesson content in Admin →
          Courses
        </Link>
      </div>

      <div className="space-y-6">
        {filtered.length === 0 ? (
          <div className="text-white/50 text-center py-10 border border-dashed border-white/10 rounded-3xl">
            No matches for "{query}".
          </div>
        ) : (
          filtered.map((c, i) => (
            <CourseSection
              key={c.id}
              course={c}
              defaultOpen={i === 0}
              onCourseChanged={reload}
            />
          ))
        )}
      </div>
    </div>
  );
}
